import type { ReactNode } from 'react'
import { ClerkProvider } from '@clerk/clerk-react'
import { koKR } from '@clerk/localizations'
import { useLang } from '../i18n'
import ClerkTokenBridge from './ClerkTokenBridge'
import { clerkAppearance } from '../clerkAppearance'

/**
 * Clerk provider that mirrors our in-app language selection and applies
 * the shared eehae appearance theme to every Clerk widget.
 *
 * Sits inside ``<LangProvider>`` so it can read the active lang via
 * React context. When the user toggles between Korean and English,
 * Clerk's embedded widgets (SignIn, SignUp, UserButton, OrganizationProfile,
 * etc.) re-render with the matching localization bundle.
 *
 * English is Clerk's default — no bundle needed. Korean uses the
 * ``koKR`` export from ``@clerk/localizations``; any untranslated
 * string falls back to English.
 *
 * The ``appearance`` prop is the single source of truth for Clerk
 * theming — see ``clerkAppearance.ts`` for the variable / element
 * overrides that map Clerk's design tokens onto eehae's paper-cream
 * + ink palette and Pretendard / Outfit type stack.
 */
export default function LocalizedClerkProvider({
  children,
  publishableKey,
}: {
  children: ReactNode
  publishableKey: string
}) {
  const lang = useLang()
  const localization = lang === 'ko' ? koKR : undefined
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={localization}
      appearance={clerkAppearance}
    >
      <ClerkTokenBridge />
      {children}
    </ClerkProvider>
  )
}
